import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse } from '@demoops/shared-types';
import { equipmentKeys } from './useEquipment';
import { invalidateAfterMove } from './useMoves';
import type { ApproveRequestData, EquipmentInbox, EquipmentMove, RequestCandidate } from '../types';

/**
 * Vic's inbox (fleet step 3, 2026-08-30).
 *
 * One query feeds the page AND the sidebar badge — same key, so the two never
 * poll separately. It refetches every minute because requests arrive from
 * other people's phones while Vic has the page open; a stale inbox is a
 * request he does not know about.
 */
export function useEquipmentInbox(enabled = true) {
  return useQuery({
    queryKey: equipmentKeys.inbox(),
    queryFn: async () => (await api.get<ApiResponse<EquipmentInbox>>('/api/v1/equipment/inbox')).data,
    enabled,
    refetchInterval: 60_000,
    retry: false,
  });
}

/** GET /equipment-requests/{id}/candidates — fetched only for the open card.
 *  A 409 means the move is no longer a request; the list says so. */
export function useRequestCandidates(requestId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...equipmentKeys.inbox(), 'candidates', requestId],
    queryFn: async () =>
      (await api.get<ApiResponse<RequestCandidate[]>>(`/api/v1/equipment-requests/${requestId}/candidates`)).data,
    enabled: enabled && !!requestId,
    retry: false,
  });
}

/** The approve envelope: the move, plus whether Vic changed the dates. */
export type ApprovedMove = EquipmentMove & { dates_changed?: boolean };

/** POST /equipment-requests/{id}/approve. A 409 names the clash; the same
 *  body again with `override_reason` wins (the other requester is told). */
export function useApproveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, ...data }: ApproveRequestData & { requestId: string }) =>
      (await api.post<ApiResponse<ApprovedMove>>(`/api/v1/equipment-requests/${requestId}/approve`, data)).data,
    onSuccess: (move, vars) => invalidateAfterMove(qc, move?.equipment_id ?? vars.equipment_id),
  });
}

/** POST /equipment-requests/{id}/decline — with a reason the requester reads. */
export function useDeclineRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason: string }) =>
      (await api.post<ApiResponse<EquipmentMove>>(`/api/v1/equipment-requests/${requestId}/decline`, { reason })).data,
    onSuccess: (move) => invalidateAfterMove(qc, move?.equipment_id),
  });
}
